import { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/context/BrandContext";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import GameweekBanner from "@/components/GameweekBanner";
import { useSlugScope } from "@/lib/scoped";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ResetPasswordDialog } from "@/components/ResetPasswordDialog";
import { toast } from "sonner";
import { ShieldAlert, ArrowRight, Trophy, LogOut } from "lucide-react";

/**
 * Message shown to non-admin users who try to sign in on the master
 * portal (`/`, `/login`). Verbatim per user directive (iter79).
 */
const MASTER_ONLY_ERROR =
  "This login is for Super-Admins only. Please use the unique URL provided by your organization (e.g. /c/[slug]) to enter the competition.";

/**
 * Top-level path segments that are NOT tenant slugs. Kept in lock-step
 * with the reserved list in `BrandContext.jsx`.
 */
const RESERVED_ROOTS = new Set([
  "login", "register", "reset", "terms", "privacy", "cookies",
  "unsubscribe", "business", "redeem", "c", "client",
  "editor", "settings", "admin",
  "leaderboard", "leagues", "matches",
  "champions", "seasons-archive", "payments",
]);

/**
 * LoginPage
 *
 * Reused across three surfaces:
 *   1. `/login`, `/reset/:token`, `/` (RootRoute fallback) — the MASTER
 *      Control Centre. `masterOnly` is set → only role=admin can sign in
 *      here. Any non-admin who authenticates is immediately signed out
 *      and shown the MASTER_ONLY_ERROR banner.
 *   2. `/[slug]` via `SluggedLoginPage` — branded pub login, no lock.
 *   3. `/c/[slug]` via `ClientSlugHome` — branded pub login, no lock.
 *
 * Locked surfaces show the error inline (persistent) AND as a toast, so
 * clearing the toast doesn't hide the reason for the block.
 */
export default function LoginPage({ masterOnly = false }) {
  const { login, user, logout } = useAuth();
  const { brand } = useBrand();
  const navigate = useNavigate();
  const location = useLocation();
  const { token: resetToken } = useParams();
  // "Effective slug" for the login page = the slug from the URL when we're
  // inside `/c/[slug]/*` or on a bare `/[slug]` landing, otherwise the
  // brand's slug (set by SluggedLoginPage when the user landed at `/[slug]`).
  // Either way we keep every link inside the same branded environment and,
  // critically, we don't race BrandContext hydration when deciding the
  // post-login redirect target (iter88 bug fix — editor logging in on
  // /founders-arms was redirected to `/` when brand?.slug hadn't populated
  // by the time onSubmit resolved, triggering the master-only logout).
  const urlSlugFromCtx = useSlugScope();
  const bareSlugMatch = location.pathname.match(/^\/([a-z0-9-]+)\/?$/i);
  const bareSlug =
    bareSlugMatch && !RESERVED_ROOTS.has(bareSlugMatch[1].toLowerCase())
      ? bareSlugMatch[1].toLowerCase()
      : null;
  const urlSlug = urlSlugFromCtx || bareSlug;
  const activeSlug = urlSlug || brand?.slug || null;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [sponsorship, setSponsorship] = useState(null);
  const [masterOnlyError, setMasterOnlyError] = useState(false);
  // iter80 — "Not a Super-Admin? Enter your pub URL →" helper. Shown
  // below the SUPER-ADMINS ONLY banner. Lets a locked-out client type
  // their org slug (or full pub URL) and be routed to `/[slug]` in one
  // click, removing the customer-support friction of "which URL do I
  // actually use?". Slug is normalised (strips scheme/host/leading `/`)
  // and validated via the public `/api/pubs/by-slug/{slug}` before
  // navigation — a missing pub surfaces an inline error instead of
  // dumping the user onto an unbranded fallback page.
  const [pubUrlHelperOpen, setPubUrlHelperOpen] = useState(false);
  const [pubUrlInput, setPubUrlInput] = useState("");
  const [pubUrlLookupError, setPubUrlLookupError] = useState("");
  const [pubUrlLoading, setPubUrlLoading] = useState(false);

  // Normalise anything the user pastes — full URL, `/founders-arms`,
  // `Founders-Arms`, or `founders-arms` — into a lowercase bare slug.
  const normaliseSlugInput = (raw) => {
    let s = (raw || "").trim();
    // Strip protocol + host if a full URL was pasted.
    if (/^https?:\/\//i.test(s)) {
      try {
        s = new URL(s).pathname || "";
      } catch {
        // Leave as-is if URL parsing fails.
      }
    }
    // Drop leading `/c/` prefix if present, then any leading slash.
    s = s.replace(/^\/+/, "");
    s = s.replace(/^c\//i, "");
    // Take only the first path segment — everything after the first `/`
    // (e.g. `/leaderboard`) is irrelevant to the landing target.
    s = s.split("/")[0] || "";
    // Drop query strings / hashes.
    s = s.split("?")[0].split("#")[0];
    return s.toLowerCase();
  };

  const submitPubUrl = async (e) => {
    e?.preventDefault?.();
    setPubUrlLookupError("");
    const slug = normaliseSlugInput(pubUrlInput);
    if (!slug) {
      setPubUrlLookupError("Enter your pub URL (e.g. founders-arms).");
      return;
    }
    if (!/^[a-z0-9-]+$/.test(slug)) {
      setPubUrlLookupError("Pub URL can only contain letters, numbers and dashes.");
      return;
    }
    setPubUrlLoading(true);
    try {
      await api.get(`/pubs/by-slug/${encodeURIComponent(slug)}`);
      // 200 = pub exists. Send them to the bare-slug branded login.
      navigate(`/${slug}`, { replace: false });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        setPubUrlLookupError(
          `We couldn't find a pub called "${slug}". Double-check with your organiser.`,
        );
      } else {
        setPubUrlLookupError("Something went wrong — please try again in a moment.");
      }
    } finally {
      setPubUrlLoading(false);
    }
  };

  // Iter79 — if an already-authenticated non-admin lands on the master
  // login (via bookmark, share, back button…), they don't belong here.
  //
  // iter95.5 — SESSION CHOOSER (was auto-redirect).
  //
  // Previously any non-admin session on a masterOnly surface would
  // silently `navigate()` the user to their own slug, which had two
  // painful consequences:
  //   • the super-admin couldn't sign in from `/` while a stale slug-
  //     editor cookie existed in the browser (they were bounced away
  //     before the login form even rendered);
  //   • a slug-editor's session leaked onto the Dugsy master brand in
  //     any second window that happened to be sitting on `/`.
  //
  // Now: no auto-navigation. If a non-admin lands here, we show a
  // "Currently signed in as X" chip above the login form with two
  // explicit choices — Continue to their dashboard OR Log out. The
  // login form itself stays visible so the super-admin can log in
  // straight away.
  //
  // The only exception is `/reset/:token` — magic-link password reset.
  // On that flow the user is still anonymous when they arrive (the
  // token authenticates them via a modal), so no chip is needed.
  const showSessionChooser =
    masterOnly && !!user && user.role !== "admin" && !resetToken;
  // iter99 — always show the "SUPER-ADMINS ONLY" red banner on the
  // master surface whenever a non-admin is signed in (in addition to
  // the failed-login banner state). Ensures anyone bounced from
  // `/leaderboard`, `/settings`, `/champions`, etc. by
  // ProtectedRoute's master-portal lockdown sees the message even
  // without hitting the login form.
  const showMasterOnlyBanner =
    masterOnlyError || (masterOnly && !!user && user.role !== "admin" && !resetToken);
  const sessionChooserDestination = user
    ? (user.is_editor && (user.pub_slug || user.client_slug)
        // iter144 — editor lands on Predictions on branded surface.
        ? `/c/${user.pub_slug || user.client_slug}/matches`
        : user.pub_slug
        ? `/c/${user.pub_slug}`
        : user.role === "client"
        ? "/client"
        : user.is_editor
        ? "/editor"
        : null)
    : null;

  // iter176 — When AuthContext detects a 401 on any authenticated call,
  // it navigates here with `?session=expired`. Toast on mount so the
  // user knows WHY they got bounced back to the login page. Strip the
  // param afterwards so a reload doesn't re-toast.
  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    if (params.get("session") === "expired") {
      // Defer one tick: on the master surface LoginPage mounts in the SAME
      // commit as App's <Toaster/>, and child effects run BEFORE the parent's,
      // so a toast fired synchronously here is published before Sonner's
      // Toaster has subscribed and is silently dropped.
      setTimeout(
        () => toast.error("Session expired — please sign in again", { duration: 6000 }),
        0,
      );
      params.delete("session");
      const cleaned = params.toString();
      const clean = cleaned ? `${location.pathname}?${cleaned}` : location.pathname;
      // Replace so the flag doesn't stay in the browser history / reload noise.
      navigate(clean, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-open the reset dialog when arriving via /reset/:token (magic link)
  useEffect(() => {
    if (resetToken) {
      setResetOpen(true);
    }
  }, [resetToken]);

  // Fetch editor-configured sponsorship (public endpoint). When enabled,
  // replaces the default stadium hero on this landing/login page — same
  // behaviour as /register so the branding is consistent.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/registration-sponsorship");
        if (!cancelled) setSponsorship(data);
      } catch (err) {
        // Non-critical — login page still renders with the default hero on failure.
        if (typeof console !== "undefined") console.debug("[login] sponsorship fetch failed:", err?.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMasterOnlyError(false);
    // iter79 — when the login page is a master-only surface (`/`, `/login`,
    // `/reset/:token`), pass masterOnly through to AuthContext.login so
    // non-admin sessions are NEVER persisted. This avoids the RootRoute
    // remount race that previously wiped `masterOnlyError` on `/` (iter73
    // testing-agent finding).
    const res = await login(email, password, { masterOnly });
    setLoading(false);
    if (res.master_locked) {
      // iter150 — If the master-blocked user is assigned to a specific
      // pub, whisk them to their branded login instead of showing the
      // "Super-Admins only" banner. Their next credential entry will
      // succeed there. Fallback to the banner if there's no assignment.
      const targetSlug =
        (res.user?.pub_slug || res.user?.client_slug || "").trim().toLowerCase();
      if (targetSlug) {
        toast.info(`Please sign in on your league page (${targetSlug}).`, {
          duration: 6000,
        });
        navigate(`/c/${targetSlug}/login`, { replace: true });
        return;
      }
      // Non-admin authenticated on a locked surface — banner + toast.
      // The session was never persisted, so LoginPage stays mounted and
      // the banner render is stable across all master-only routes.
      setMasterOnlyError(true);
      toast.error(MASTER_ONLY_ERROR, { duration: 8000 });
      return;
    }
    if (res.ok) {
      const isAdmin = res.user?.role === "admin";
      const isEditor = !isAdmin && !!res.user?.is_editor;
      const isClient = !isAdmin && res.user?.role === "client";
      toast.success("Welcome back");
      // Re-derive the slug at redirect time from the LIVE URL — the
      // render-scoped `activeSlug` closure could be stale if BrandContext
      // hydration or a nav-effect kicked in during the login await.
      const livePath = window.location.pathname;
      const scopedMatch = livePath.match(/^\/c\/([a-z0-9-]+)(?:\/|$)/i);
      const bareMatch = livePath.match(/^\/([a-z0-9-]+)\/?$/i);
      const liveBareSlug =
        bareMatch && !RESERVED_ROOTS.has(bareMatch[1].toLowerCase())
          ? bareMatch[1].toLowerCase()
          : null;
      const liveSlug = scopedMatch ? scopedMatch[1].toLowerCase() : liveBareSlug;
      const slug = liveSlug || brand?.slug || null;
      // Post-login redirect priority:
      //   1. explicit `location.state.from` (protected-route came-from), OR
      //   2. Super-Admin always goes to `/` — they manage every tenant, so
      //      pinning them inside a specific client slug even when a brand
      //      is active in the URL would trap them in that tenant view.
      //   3. `/c/<slug>/editor` for Editors when signing in from any
      //      branded surface (bug-fix iter88 — no more '/' bounce that
      //      triggered the master-only logout race), else `/editor`.
      //   4. `/c/<slug>/editor` for role=client on a branded surface —
      //      that's their ClientDashboardPage. Off-brand → `/client`.
      //   5. `/c/<slug>` for regular users when signing in from a branded
      //      landing so they stay inside their pub's white-labelled
      //      experience.
      //   6. `/` (global landing) for platform-level logins.
      let redirect;
      if (location.state?.from) {
        redirect = location.state.from;
      } else if (isAdmin) {
        redirect = "/";
      } else if (isEditor) {
        // iter144 — pub editor lands on Predictions on a branded surface
        // (per user directive). The editor's own admin dashboard is still
        // reachable via the header menu → `/c/[slug]/editor`. On a
        // non-branded (master) login they still land on `/editor`.
        redirect = slug ? `/c/${slug}/matches` : "/editor";
      } else if (isClient) {
        redirect = slug ? `/c/${slug}/editor` : "/client";
      } else if (slug) {
        redirect = `/c/${slug}`;
      } else {
        redirect = "/";
      }
      navigate(redirect, { replace: true });
    } else {
      toast.error(res.error);
    }
  };

  // Iter74 → iter132 — sponsorship isolation.
  //
  // Historically the global sponsorship panel (from `/api/registration-
  // sponsorship`) fell back onto branded `/[slug]` pages when the pub
  // had no per-pub content. In practice this leaked super-admin
  // sponsorship copy/logos onto branded pub logins that are meant to
  // be strictly ring-fenced. The correct behaviour is: global panel
  // renders ONLY on the platform-wide (non-branded) login. Per-pub
  // pages render per-pub content or nothing at all.
  const globalEnabled = !brand && !!sponsorship?.enabled;
  const globalMessage = globalEnabled ? (sponsorship?.message || "") : "";
  const globalSponsors = globalEnabled ? (sponsorship?.sponsors || []) : [];
  const globalOrgLogo = globalEnabled ? sponsorship?.organisation_logo_url : null;

  const brandLogoBg = brand
    ? (brand.logo_url
        ? (brand.logo_url.startsWith("http") ? brand.logo_url : `${process.env.REACT_APP_BACKEND_URL}${brand.logo_url}`)
        : brand.logo_data_url) || null
    : null;

  // iter91 — "Current prize" chip on the branded pre-login screen. Now
  // shows FOUR sources of value, each of which is opt-in:
  //   1. Match-day prize fund (£X)        — `current_match_day_prize_pence`
  //   2. Season / overall league prize (£X) — `prize_fund_pence`
  //   3. Voucher count + description       — `voucher_count` × `prize_pool_text`
  //   4. Voucher expiry date (iter93)      — `voucher_expires_at` (shown as
  //      "expires 01 Aug 2026" next to the voucher line, only when set)
  // Anything the admin left at 0 / empty is silently hidden so the chip
  // stays compact — a pub with only a voucher shows just that, a pub
  // with everything shows all three rows.
  const brandMatchDayPence = Number(brand?.current_match_day_prize_pence || 0);
  const brandSeasonPence = Number(brand?.prize_fund_pence || 0);
  const brandVoucherCount = Number(brand?.voucher_count || 0);
  const brandVoucherText = (brand?.prize_pool_text || "").trim();
  // iter148 — vouchers only render when BOTH text is set AND count > 0.
  // Voucher count = 0 = "bragging-rights only" league, no voucher chip.
  const showVoucher = !!brandVoucherText && brandVoucherCount > 0;
  // iter147 — % of entry fees mode. When the pub uses "percent", the
  // effective season pot is the projected value computed server-side
  // from paid league_entry transactions × percent.
  const seasonPrizeType = (brand?.season_prize_type || "fixed").trim();
  const seasonPrizePercent = Number(brand?.season_prize_percent || 0);
  const projectedSeasonPence = Number(brand?.projected_season_prize_pence || 0);
  const effectiveSeasonPence =
    seasonPrizeType === "percent" ? projectedSeasonPence : brandSeasonPence;
  const showSeasonPrize =
    seasonPrizeType === "percent" ? seasonPrizePercent > 0 : brandSeasonPence > 0;
  // iter146 — describe the pub's prize-split so the login chip shows
  // "Winner takes all" vs "1st 50% · 2nd 30% · 3rd 20%" (or custom).
  const brandPayoutMode = (brand?.payout_mode || "winner_all").trim();
  const brandPayoutSplit = brand?.payout_split || null;
  const prizeSplitLabel = (() => {
    switch (brandPayoutMode) {
      case "50_30_20":
        return "1st 50% · 2nd 30% · 3rd 20%";
      case "custom": {
        const s = brandPayoutSplit || {};
        return `1st ${s.first ?? 0}% · 2nd ${s.second ?? 0}% · 3rd ${s.third ?? 0}%`;
      }
      case "voucher_qr":
        return "Voucher prize";
      case "winner_takes_all":
      case "winner_all":
      default:
        return "Winner takes all";
    }
  })();
  const showPrizeChip = !!(
    brand && (brandMatchDayPence > 0 || showSeasonPrize || showVoucher)
  );
  // iter147 — fallback chip on branded surfaces with NO monetary prize
  // and NO voucher — shows a friendly "Bragging rights only :)" note so
  // the login page still communicates what's on offer.
  const showBraggingRightsChip = !!(brand && !showPrizeChip);
  const formatPounds = (pence) => `£${(pence / 100).toFixed(2).replace(/\.00$/, "")}`;

  // Pub-specific sponsors (per-pub, editable in the Client Dashboard's
  // Local Sponsors card). When any are present they win over the global
  // panel — the pub explicitly opted in.
  const pubSponsors = brand?.sponsors || [];
  const pubSponsorshipMessage = brand?.sponsorship_message || "";
  const hasPerPubMessage = !!(brand && pubSponsorshipMessage);
  const hasPerPubSponsors = !!(brand && pubSponsors.length > 0);

  // Whether the pale-grey panel renders at all. Now driven by whichever
  // source (per-pub OR global fallback) has something to show, plus the
  // watermark brand logo.
  const showSponsorship =
    hasPerPubMessage ||
    hasPerPubSponsors ||
    !!globalMessage ||
    !!globalOrgLogo ||
    globalSponsors.length > 0 ||
    !!brandLogoBg;

  return (
    <div className="min-h-screen bg-white grid lg:grid-cols-2">
      {/* Visual side */}
      {showSponsorship ? (
        <div
          className="hidden lg:flex relative overflow-hidden text-zinc-950"
          style={{ backgroundColor: "#F5F5F5" }}
          data-testid="login-sponsorship-panel"
        >
          <div className="relative flex flex-col p-6 sm:p-10 xl:p-14 w-full overflow-y-auto">
          {/* Global org logo — only when the pub hasn't taken over the
              panel with its own per-pub content. */}
          {!hasPerPubMessage && !hasPerPubSponsors && globalOrgLogo && (
            <img
              src={globalOrgLogo}
              alt="Organisation logo"
              data-testid="login-sponsorship-logo"
              className="h-20 sm:h-24 w-auto max-w-[240px] object-contain bg-white p-3 rounded-sm shrink-0 border border-zinc-200 mx-auto sm:mx-0"
            />
          )}

          {/* Global message shown whenever the current pub has no per-pub
              message of its own — this is the fallback path. */}
          {!hasPerPubMessage && globalMessage && (
            <p
              data-testid="login-sponsorship-message"
              className="mt-6 sm:mt-8 font-heading text-2xl sm:text-3xl xl:text-4xl font-bold leading-[1.15] tracking-tight max-w-xl text-zinc-950 text-center sm:text-left"
            >
              {globalMessage}
            </p>
          )}

          {brand && pubSponsorshipMessage && (
            <p
              data-testid="login-pub-sponsorship-message"
              className="mt-6 sm:mt-8 font-heading text-2xl sm:text-3xl xl:text-4xl font-bold leading-[1.15] tracking-tight max-w-xl text-zinc-950 text-center sm:text-left"
            >
              {pubSponsorshipMessage}
            </p>
          )}

          {((!hasPerPubSponsors && globalSponsors.length > 0) || hasPerPubSponsors || brandLogoBg) && (
            <div className="mt-10">
              {!hasPerPubSponsors && globalSponsors.length > 0 && (
                <div className="flex items-center gap-3">
                  <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-zinc-500">
                    Sponsors
                  </span>
                  <span className="h-px flex-1 bg-zinc-300" />
                </div>
              )}
              {hasPerPubSponsors && (
                <div className="flex items-center gap-3">
                  <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-zinc-500">
                    Sponsors
                  </span>
                  <span className="h-px flex-1 bg-zinc-300" />
                </div>
              )}

                {/* Relative wrapper so the brand watermark can sit behind the
                    sponsor cards. Minimum height ensures the watermark still
                    has a visible footprint on pages with no sponsors. */}
                <div
                  className="relative mt-6 min-h-[320px] sm:min-h-[420px] xl:min-h-[560px]"
                  data-testid="login-brand-watermark-slot"
                >
                  {brandLogoBg && (
                    <img
                      src={brandLogoBg}
                      alt=""
                      aria-hidden
                      data-testid="login-brand-watermark"
                      className="pointer-events-none select-none absolute inset-0 w-full h-full object-cover opacity-50"
                    />
                  )}

                  {(hasPerPubSponsors || globalSponsors.length > 0) && (
                    <ul
                      className="relative flex flex-col gap-5"
                      data-testid={hasPerPubSponsors ? "login-pub-sponsorship-list" : "login-sponsorship-list"}
                    >
                      {(hasPerPubSponsors ? pubSponsors : globalSponsors).map((s, i) => {
                        const testKey = hasPerPubSponsors ? "pub" : "global";
                        const image = (
                          <img
                            src={s.image_url}
                            alt={s.alt_text || `Sponsor ${i + 1}`}
                            className="aspect-square w-full object-contain bg-transparent rounded-sm p-3"
                          />
                        );
                        const imageBox = (
                          <div className="shrink-0 w-32 sm:w-40 xl:w-48">{image}</div>
                        );
                        const tagline = s.alt_text ? (
                          <p
                            data-testid={`login-${testKey}-sponsorship-tagline-${i}`}
                            className="font-heading text-sm sm:text-base xl:text-lg font-semibold text-zinc-800 leading-snug flex-1 min-w-0 text-center sm:text-left"
                          >
                            {s.alt_text}
                          </p>
                        ) : null;

                        // When a target_url exists, the whole row (image + tagline)
                        // is one clickable link — bigger tap target on mobile and
                        // avoids nested-<a> validation issues.
                        return (
                          <li key={s.image_url || `sponsor-${i}`} data-testid={`login-${testKey}-sponsorship-cell-${i}`}>
                            {s.target_url ? (
                              <a
                                href={s.target_url}
                                target="_blank"
                                rel="noreferrer noopener"
                                data-testid={`login-${testKey}-sponsorship-link-${i}`}
                                className="flex flex-col sm:flex-row items-center gap-3 sm:gap-5 w-full text-center sm:text-left group transition-transform hover:scale-[1.01]"
                              >
                                {imageBox}
                                {tagline}
                              </a>
                            ) : (
                              <div
                                data-testid={`login-${testKey}-sponsorship-item-${i}`}
                                className="flex flex-col sm:flex-row items-center gap-3 sm:gap-5 w-full text-center sm:text-left"
                              >
                                {imageBox}
                                {tagline}
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div
          className="hidden lg:block relative bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1661632359980-9896d88b9b7b?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400')",
          }}
          aria-hidden="true"
          data-testid="login-stadium-hero"
        >
          <div className="absolute inset-0 bg-zinc-950/30" />
          <div className="absolute bottom-12 left-12 right-12 text-white">
            <p className="font-heading text-5xl font-bold leading-[1.05] tracking-tight">
              Predict every<br />match stat.
            </p>
            <p className="mt-4 text-sm uppercase tracking-[0.25em] text-white/80">
              Goals · Shots · Corners · Offsides · Throw-ins
            </p>
          </div>
        </div>
      )}

      {/* Form side */}
      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-12">
        <div className="mb-10">
          <Logo variant="large" />
        </div>
        <div className="max-w-sm">
          <h1
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-zinc-950 leading-[1.05]"
            data-testid="login-heading"
          >
            Welcome to our Football Challenge.
          </h1>
          <p
            className="mt-4 text-base text-zinc-600 leading-relaxed"
            data-testid="login-elevator-pitch"
          >
            Predict match stats - <strong className="text-zinc-950">goals,
            shots, corners, offside, throw-ins &amp; fouls</strong> for each game selected.
          </p>
          {/* iter143 — Automated Gameweek Engine. Yellow banner sits
              immediately above the amber prize chip on the auth column,
              per user layout spec ("render above the prize banner…with a
              yellow background"). Returns null when the client hasn't
              set gameweek/match/day counts. */}
          <div className="mt-4">
            <GameweekBanner slugOverride={brand?.slug || null} variant="stacked" />
          </div>
          {showPrizeChip && (
            <div
              data-testid="login-live-prize-chip"
              className="mt-4 flex items-center gap-2 rounded-sm border border-emerald-300 bg-gradient-to-br from-emerald-50 via-emerald-100 to-emerald-50 px-3 py-2 max-w-full"
            >
              <Trophy className="h-4 w-4 text-emerald-700 shrink-0" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-xs sm:text-sm font-heading font-bold text-emerald-900 leading-tight">
                  {brandMatchDayPence > 0 && (
                    <span data-testid="login-live-prize-matchday" className="inline-flex items-baseline gap-1 whitespace-nowrap">
                      <span className="text-[9px] font-bold uppercase tracking-[0.15em] text-emerald-700">Match-day</span>
                      {formatPounds(brandMatchDayPence)}
                    </span>
                  )}
                  {brandMatchDayPence > 0 && (showSeasonPrize || showVoucher) && (
                    <span className="text-emerald-400" aria-hidden="true">·</span>
                  )}
                  {showSeasonPrize && (
                    <span data-testid="login-live-prize-season" className="inline-flex items-baseline gap-1 whitespace-nowrap">
                      <span className="text-[9px] font-bold uppercase tracking-[0.15em] text-emerald-700">Prize fund</span>
                      {formatPounds(effectiveSeasonPence)}
                      {seasonPrizeType === "percent" && (
                        <span className="text-xs sm:text-sm font-heading font-bold text-emerald-900 whitespace-nowrap ml-1" data-testid="login-live-prize-percent">
                          · ({seasonPrizePercent}% of entry fees)
                        </span>
                      )}
                      <span className="text-[10px] font-medium text-emerald-700 whitespace-nowrap ml-1" data-testid="login-live-prize-split">
                        · {prizeSplitLabel}
                      </span>
                    </span>
                  )}
                  {showSeasonPrize && showVoucher && (
                    <span className="text-emerald-400" aria-hidden="true">·</span>
                  )}
                  {showVoucher && (
                    <span data-testid="login-live-prize-voucher" className="inline-flex items-baseline gap-1 text-xs font-semibold whitespace-nowrap">
                      <span className="text-[9px] font-bold uppercase tracking-[0.15em] text-emerald-700">Vouchers</span>
                      {brandVoucherCount} × {brandVoucherText}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
          {showBraggingRightsChip && (
            <div
              data-testid="login-bragging-rights-chip"
              className="mt-4 flex items-center gap-2 rounded-sm border border-emerald-300 bg-gradient-to-br from-emerald-50 via-emerald-100 to-emerald-50 px-3 py-2 max-w-full"
            >
              <Trophy className="h-4 w-4 text-emerald-700 shrink-0" aria-hidden="true" />
              <span className="text-xs sm:text-sm font-heading font-bold text-emerald-900 leading-tight">
                Bragging rights only :)
              </span>
            </div>
          )}
          <p className="mt-2 text-sm text-zinc-500">
            Sign in to enter today&apos;s predictions.
          </p>

          <form onSubmit={onSubmit} className="mt-10 space-y-5" data-testid="login-form">
            {showSessionChooser && (
              <div
                data-testid="master-session-chooser"
                role="status"
                className="rounded-sm border border-amber-300 bg-amber-50 text-amber-900 px-4 py-3"
              >
                <p className="text-sm leading-snug">
                  <strong className="block font-semibold uppercase tracking-[0.1em] text-xs mb-1">
                    Currently signed in as
                  </strong>
                  <span data-testid="master-session-chooser-email" className="font-medium">
                    {user.email}
                  </span>
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {sessionChooserDestination && (
                    <button
                      type="button"
                      data-testid="master-session-chooser-continue"
                      onClick={() => navigate(sessionChooserDestination, { replace: true })}
                      className="inline-flex items-center gap-1.5 rounded-sm border border-amber-400 bg-white px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.1em] text-amber-900 hover:bg-amber-100"
                    >
                      Continue to my dashboard
                      <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  )}
                  <button
                    type="button"
                    data-testid="master-session-chooser-logout"
                    onClick={async () => {
                      await logout();
                      toast.success("Signed out. You can now sign in as Super-Admin.");
                    }}
                    className="inline-flex items-center gap-1.5 rounded-sm border border-red-400 bg-white px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.1em] text-red-900 hover:bg-red-50"
                  >
                    <LogOut className="h-3.5 w-3.5" aria-hidden="true" />
                    Log out &amp; sign in as Super-Admin
                  </button>
                </div>
              </div>
            )}
            {showMasterOnlyBanner && (
              <div
                data-testid="master-only-error"
                role="alert"
                className="rounded-sm border border-red-300 bg-red-50 text-red-900 px-4 py-3"
              >
                <div className="flex items-start gap-3">
                  <ShieldAlert className="h-5 w-5 shrink-0 mt-0.5" aria-hidden="true" />
                  <p className="text-sm leading-snug">
                    <strong className="block font-semibold uppercase tracking-[0.1em] text-xs mb-1">
                      Super-Admins only
                    </strong>
                    {MASTER_ONLY_ERROR}
                  </p>
                </div>
                <div className="mt-3 pt-3 border-t border-red-200">
                  {!pubUrlHelperOpen ? (
                    <button
                      type="button"
                      onClick={() => setPubUrlHelperOpen(true)}
                      data-testid="pub-url-helper-toggle"
                      className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.1em] text-red-900 hover:text-red-950 underline-offset-4 hover:underline"
                    >
                      Not a Super-Admin? Enter your pub URL
                      <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  ) : (
                    <div data-testid="pub-url-helper-form" className="space-y-2">
                      <Label
                        htmlFor="pub-url-helper-input"
                        className="text-xs font-bold uppercase tracking-[0.15em] text-red-900"
                      >
                        Your pub URL
                      </Label>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <span
                            aria-hidden="true"
                            className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-red-900/50 select-none"
                          >
                            /
                          </span>
                          <Input
                            id="pub-url-helper-input"
                            data-testid="pub-url-helper-input"
                            value={pubUrlInput}
                            onChange={(e) => {
                              setPubUrlInput(e.target.value);
                              if (pubUrlLookupError) setPubUrlLookupError("");
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                submitPubUrl();
                              }
                            }}
                            placeholder="founders-arms"
                            autoComplete="off"
                            spellCheck={false}
                            className="h-10 pl-6 rounded-sm border-red-300 bg-white text-red-950 placeholder:text-red-900/40 focus-visible:ring-red-500"
                          />
                        </div>
                        <Button
                          type="button"
                          onClick={submitPubUrl}
                          disabled={pubUrlLoading}
                          data-testid="pub-url-helper-submit"
                          className="h-10 rounded-sm bg-red-900 hover:bg-red-950 text-white font-semibold tracking-tight"
                        >
                          {pubUrlLoading ? "Checking…" : "Go"}
                        </Button>
                      </div>
                      {pubUrlLookupError && (
                        <p
                          data-testid="pub-url-helper-error"
                          className="text-xs text-red-900 mt-1"
                        >
                          {pubUrlLookupError}
                        </p>
                      )}
                      <p className="text-[11px] leading-snug text-red-900/70">
                        Paste the link your organiser sent you — e.g.{" "}
                        <span className="font-mono">play.amblemead.biz/founders-arms</span>{" "}
                        — or just the pub name.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Email</Label>
              <Input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="mt-2 h-12 rounded-sm border-zinc-300 focus-visible:ring-[#0038FF]"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Password</Label>
              <Input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="mt-2 h-12 rounded-sm border-zinc-300 focus-visible:ring-[#0038FF]"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full h-12 rounded-sm text-white font-semibold tracking-tight active:scale-[0.98] transition-colors"
              style={{ backgroundColor: brand?.primary_color || "#0038FF" }}
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(activeSlug ? `/c/${activeSlug}/register` : "/register")}
              data-testid="create-account-btn"
              className="h-12 rounded-sm border-zinc-950 border-2 text-zinc-950 font-semibold tracking-tight hover:bg-zinc-950 hover:text-white active:scale-[0.98] transition-colors"
            >
              Create an account
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setResetOpen(true)}
              data-testid="reset-password-btn"
              className="h-12 rounded-sm border-zinc-300 text-zinc-700 font-semibold tracking-tight hover:border-zinc-950 hover:text-zinc-950 active:scale-[0.98] transition-colors"
            >
              Reset password
            </Button>
          </div>

          <p className="mt-10 text-xs text-zinc-400 leading-relaxed" data-testid="login-legal-links">
            By using Dugsy you agree to our{" "}
            <a href={activeSlug ? `/c/${activeSlug}/terms` : "/terms"} className="font-semibold text-zinc-600 hover:text-zinc-950 underline underline-offset-2" data-testid="login-terms-link">Terms of Service</a>{" "}
            and{" "}
            <a href={activeSlug ? `/c/${activeSlug}/privacy` : "/privacy"} className="font-semibold text-zinc-600 hover:text-zinc-950 underline underline-offset-2" data-testid="login-privacy-link">Privacy Policy</a>.
          </p>
        </div>
      </div>

      <ResetPasswordDialog
        open={resetOpen}
        onOpenChange={(v) => {
          setResetOpen(v);
          // Clean the URL after closing so the token isn't left in the address bar
          if (!v && resetToken) navigate("/login", { replace: true });
        }}
        initialToken={resetToken}
      />
    </div>
  );
}
